import { isUnlimited, maxWorkspacesFor, type PlanId } from '@qr/shared/plans';
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';
import { api, messageFromError } from '../lib/api.js';
import { db } from '../lib/firebase.js';
import { useOrg } from '../lib/org.js';
import { usePedirTexto } from '../lib/dialogos.js';

/**
 * Selector de espacio de trabajo.
 *
 * Es un control pequeno con mucho poder: decide que realidad ve el resto del
 * panel. Si alguien cambia el destino de un cartel creyendo que trabaja en el
 * espacio de otro cliente, el dano ya esta hecho y ademas esta impreso. Por eso
 * el espacio abierto se muestra siempre, aunque solo haya uno, y el que esta
 * activo se marca de forma explicita dentro de la lista.
 */
export function OrgSwitcher() {
  const pedirTexto = usePedirTexto();
  const org = useOrg();
  const [open, setOpen] = useState(false);
  const [negocios, setNegocios] = useState<Record<string, { nombre: string; plan: PlanId }>>({});
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const container = useRef<HTMLDivElement>(null);

  /*
   * De cada negocio se leen dos cosas de su documento, porque en el de miembro
   * solo esta el rol: el nombre, para pintarlo, y el plan, porque de ahi sale
   * cuantos negocios caben.
   */
  useEffect(() => {
    const sources = org.memberships.length > 0
      ? org.memberships
      : org.orgId
        ? [{ orgId: org.orgId }]
        : [];
    const unsubs = sources.map((m) =>
      onSnapshot(doc(db, `orgs/${m.orgId}`), (snap) => {
        const d = snap.data();
        setNegocios((prev) => ({
          ...prev,
          [m.orgId]: {
            nombre: (d?.['name'] as string) ?? '',
            plan: (d?.['plan'] as PlanId) ?? 'free',
          },
        }));
      }),
    );
    return () => unsubs.forEach((u) => u());
  }, [org.memberships, org.orgId]);

  // Cerrar al hacer clic fuera y con Escape: un menu que se queda abierto tapa
  // justo lo que el usuario iba a mirar.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  async function createSpace() {
    const name = await pedirTexto({
      titulo: 'Crear otro negocio',
      etiqueta: 'Nombre del negocio',
      ayuda: 'Tendrá sus propios códigos, su equipo y su plan, aparte de los demás.',
      aceptar: 'Crear',
      maximo: 60,
    });
    if (!name) return;

    setError(null);
    setCreating(true);
    try {
      await api.createOrg({ name: name.trim() });
      setOpen(false);
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setCreating(false);
    }
  }

  const current = org.name || 'Mi negocio';

  /**
   * Si la consulta de membresias no devuelve nada pero hay un espacio abierto
   * (cuentas anteriores al selector), se muestra ese. Un menu vacio estando
   * dentro de un espacio es peor que no tener menu: parece que se ha perdido
   * el acceso.
   */
  const entries =
    org.memberships.length > 0
      ? org.memberships
      : org.orgId
        ? [{ orgId: org.orgId, role: org.role ?? 'viewer' }]
        : [];

  /*
   * Cuantos negocios caben, con la misma cuenta que hace el servidor.
   *
   * Solo cuentan los que uno posee: estar invitado al negocio de otro no gasta
   * cupo, porque el cupo lo paga su dueno. Y manda el plan mas generoso de los
   * que ya tiene, que es lo que hace createOrg.
   *
   * Esto no sustituye al limite del servidor —quien llame a la API a mano se
   * encuentra el mismo no—, pero evita ofrecer un boton que solo lleva a un
   * error. Mientras los planes no hayan cargado se deja pasar: el servidor
   * tiene la ultima palabra y es preferible un error raro a bloquear a alguien
   * que si puede.
   */
  const propios = org.memberships.filter((m) => m.role === 'owner');
  const planesPropios = propios
    .map((m) => negocios[m.orgId]?.plan)
    .filter((plan): plan is PlanId => Boolean(plan));
  const topeConocido = planesPropios.length === propios.length && propios.length > 0;
  const tope = maxWorkspacesFor(planesPropios);
  const sinCupo = topeConocido && !isUnlimited(tope) && propios.length >= tope;

  return (
    <div className="switcher" ref={container}>
      <button
        type="button"
        className="switcher__trigger"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {/* La inicial del espacio hace de ancla visual: con varios clientes
            abiertos, el color y la letra se reconocen antes que el nombre. */}
        <span
          className="grid size-7 shrink-0 place-items-center rounded-sm bg-brand text-[12px] font-bold text-brand-ink"
          aria-hidden="true"
        >
          {current.slice(0, 1).toUpperCase()}
        </span>
        <span className="switcher__current min-w-0">
          <strong className="truncate">{current}</strong>
          <span className="switcher__plan truncate">
            {org.isComped ? 'Acceso completo' : org.planName}
          </span>
        </span>
        <span className="switcher__caret" aria-hidden="true">
          &#9662;
        </span>
      </button>

      {open ? (
        <div className="switcher__menu" role="menu">
          <p className="switcher__label">Tus negocios</p>

          <ul>
            {entries.map((m) => {
              const active = m.orgId === org.orgId;
              return (
                <li key={m.orgId}>
                  <button
                    type="button"
                    role="menuitem"
                    className={`switcher__item${active ? ' switcher__item--active' : ''}`}
                    onClick={async () => {
                      if (!active) await org.switchTo(m.orgId);
                      setOpen(false);
                    }}
                  >
                    <span className="switcher__name">
                      {negocios[m.orgId]?.nombre ?? 'Cargando...'}
                    </span>
                    <span className="switcher__role">{ROLE_LABELS[m.role]}</span>
                    {active ? (
                      <span className="switcher__check" aria-label="Negocio abierto">
                        &#10003;
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>

          {error ? <p className="switcher__error">{error}</p> : null}

          {sinCupo ? (
            <p className="switcher__tope">
              {tope === 1
                ? 'Tu plan incluye un negocio. Para llevar varios, cambia de plan.'
                : `Tu plan incluye ${tope} negocios y ya los tienes todos.`}
            </p>
          ) : (
            <button
              type="button"
              className="switcher__create"
              onClick={createSpace}
              disabled={creating}
            >
              {creating ? 'Creando...' : '+ Crear otro negocio'}
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'Propietario',
  admin: 'Administrador',
  editor: 'Editor',
  viewer: 'Lectura',
};
